import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Import types
import type {
  DragMode,
  EraseHover,
  EventBlock,
  EventByUser,
} from '../../types/schedule';

// Import utils
import { getQuarterHours } from '../../utils/date';

// Import hooks
import useEventsByUser from './useEventsByUser';

export type GridInteractionsParams = {
  users: string[]; // userIds
  currentUser: string; // userId
  ownerByUserId: Record<string, string>;
  deviceId: string;
  ownsParticipant: (ownerDeviceId: string | undefined) => boolean;
  eventsByUser: EventByUser;
  setEventsByUser: React.Dispatch<React.SetStateAction<EventByUser>>;
  gridStart: string; // HH:MM
  gridEnd: string; // HH:MM
  isOverlapTimes: boolean;
  isScheduleEventMode?: boolean;
};

/**
 * Encapsulates drag/add/erase/hover logic for the availability grid.
 * Integrates with useEventsByUser to mutate EventBlock[] per user.
 */
const useGridInteractions = (params: GridInteractionsParams) => {
  const {
    users,
    currentUser,
    ownerByUserId,
    ownsParticipant,
    eventsByUser,
    setEventsByUser,
    gridStart,
    gridEnd,
    isOverlapTimes,
    isScheduleEventMode = false,
  } = params;

  const canEdit = !!currentUser && ownsParticipant(ownerByUserId[currentUser]);
  const TIMES = useMemo(
    () => getQuarterHours(gridStart, gridEnd),
    [gridStart, gridEnd],
  );

  const { addSegment, eraseAt } = useEventsByUser(
    eventsByUser,
    setEventsByUser,
    (userId) => ownsParticipant(ownerByUserId[userId]),
  );

  const isDragging = useRef(false);
  const dragDate = useRef<string | null>(null);
  const dragStartIndex = useRef<number | null>(null);
  const dragMode = useRef<DragMode>(null); // 'add' | 'erase' | null

  const [draftEvent, setDraftEvent] = useState<EventBlock | null>(null);
  const [eraseHover, setEraseHover] = useState<EraseHover | null>(null);
  const [hoveredUsers, setHoveredUsers] = useState<string[]>([]);
  const [scheduleEventSelection, setScheduleEventSelection] = useState<EventBlock | null>(null);
  const lastErased = useRef<{ dateKey: string; row: number } | null>(null);

  // Index for O(1) membership checks: user -> (dateKey -> Set(rows))
  const rowsByUserDate = useMemo(() => {
    const byUser = new Map<string, Map<string, Set<number>>>();
    for (const u of users) {
      const segments = eventsByUser[u] || [];
      const byDate = new Map<string, Set<number>>();
      for (const seg of segments) {
        let set = byDate.get(seg.dateKey);
        if (!set) {
          set = new Set<number>();
          byDate.set(seg.dateKey, set);
        }
        const start = Math.max(0, seg.startIndex);
        const end = Math.min(TIMES.length, Math.max(start, seg.endIndex));
        for (let r = start; r < end; r++) set.add(r);
      }
      byUser.set(u, byDate);
    }
    return byUser;
  }, [users, eventsByUser, TIMES.length]);

  const getUsersAtCell = useCallback(
    (dateKey: string, row: number) => {
      const names: string[] = [];
      for (const u of users) {
        const has = rowsByUserDate.get(u)?.get(dateKey)?.has(row) ?? false;
        if (has) names.push(u);
      }
      return names;
    },
    [users, rowsByUserDate],
  );

  const currentUserHas = useCallback(
    (dateKey: string, row: number) => {
      return rowsByUserDate.get(currentUser)?.get(dateKey)?.has(row) ?? false;
    },
    [rowsByUserDate, currentUser],
  );

  const onDown = useCallback(
    (_e: React.PointerEvent<HTMLElement>, dateKey: string, row: number) => {
      // Schedule event mode: allow selection for anyone (works in both normal and overlap-times mode)
      if (isScheduleEventMode) {
        isDragging.current = true;
        dragDate.current = dateKey;
        dragStartIndex.current = row;
        dragMode.current = 'add';
        setScheduleEventSelection({
          id: crypto.randomUUID(),
          dateKey,
          startIndex: row,
          endIndex: row + 1,
        });
        return;
      }

      // Normal edit mode: only works when not in overlap-times mode
      if (isOverlapTimes) return;
      if (!canEdit) return;
      // Do NOT capture the pointer here. We rely on onPointerEnter firing
      // on sibling cells while dragging to extend the selection. Pointer capture
      // would redirect subsequent pointer events to the original element and
      // prevent cross-cell enter events, breaking drag-to-add behavior.
      isDragging.current = true;
      dragDate.current = dateKey;
      dragStartIndex.current = row;

      const has = currentUserHas(dateKey, row);
      dragMode.current = has ? 'erase' : 'add';
      if (dragMode.current === 'add') {
        setDraftEvent({
          id: crypto.randomUUID(),
          dateKey,
          startIndex: row,
          endIndex: row + 1,
        });
      } else {
        // erase immediately
        if (
          !lastErased.current ||
          lastErased.current.dateKey !== dateKey ||
          lastErased.current.row !== row
        ) {
          eraseAt(currentUser, dateKey, row);
          lastErased.current = { dateKey, row };
        }
        setEraseHover({ dateKey, row });
      }
    },
    [canEdit, isOverlapTimes, isScheduleEventMode, currentUserHas, eraseAt, currentUser],
  );

  const onEnter = useCallback(
    (dateKey: string, row: number) => {
      if (!isDragging.current) return;
      if (dragDate.current !== dateKey) return; // do not span multiple dates
      
      // Schedule event mode: update selection
      if (isScheduleEventMode && dragMode.current === 'add') {
        const s = dragStartIndex.current ?? row;
        const start = Math.min(s, row);
        const end = Math.max(s, row) + 1;
        setScheduleEventSelection((sel) =>
          sel
            ? { ...sel, startIndex: start, endIndex: Math.min(end, TIMES.length) }
            : {
                id: crypto.randomUUID(),
                dateKey,
                startIndex: start,
                endIndex: Math.min(end, TIMES.length),
              },
        );
        return;
      }

      if (dragMode.current === 'add' && draftEvent) {
        const s = dragStartIndex.current ?? row;
        const start = Math.min(s, row);
        const end = Math.max(s, row) + 1;
        setDraftEvent((d) =>
          d
            ? { ...d, startIndex: start, endIndex: Math.min(end, TIMES.length) }
            : d,
        );
      } else if (dragMode.current === 'erase') {
        if (
          !lastErased.current ||
          lastErased.current.dateKey !== dateKey ||
          lastErased.current.row !== row
        ) {
          eraseAt(currentUser, dateKey, row);
          lastErased.current = { dateKey, row };
        }
        setEraseHover({ dateKey, row });
      }
    },
    [draftEvent, TIMES.length, eraseAt, currentUser, isScheduleEventMode],
  );

  // Commit or cancel on global pointerup/pointercancel
  useEffect(() => {
    const finish = () => {
      if (!isDragging.current) return;
      
      // Schedule event mode: don't commit to eventsByUser, just keep selection
      if (isScheduleEventMode) {
        isDragging.current = false;
        dragDate.current = null;
        dragStartIndex.current = null;
        dragMode.current = null;
        return;
      }

      if (dragMode.current === 'add' && draftEvent) {
        const { dateKey, startIndex, endIndex } = draftEvent;
        addSegment(currentUser, dateKey, startIndex, endIndex);
      }
      isDragging.current = false;
      dragDate.current = null;
      dragStartIndex.current = null;
      dragMode.current = null;
      setDraftEvent(null);
      setEraseHover(null);
      lastErased.current = null;
    };
    const cancel = () => {
      if (!isDragging.current) return;
      // Cancel behaves like finishing without committing add draft
      isDragging.current = false;
      dragDate.current = null;
      dragStartIndex.current = null;
      dragMode.current = null;
      if (!isScheduleEventMode) {
        setDraftEvent(null);
        setEraseHover(null);
        lastErased.current = null;
      }
    };
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
    };
  }, [addSegment, currentUser, draftEvent, isScheduleEventMode]);

  const startHover = useCallback(
    (dateKey: string, row: number, _e: React.MouseEvent) => {
      setHoveredUsers(getUsersAtCell(dateKey, row));
    },
    [getUsersAtCell],
  );
  const stopHover = useCallback(() => setHoveredUsers([]), []);
  const moveHover = useCallback(() => {}, []);

  return {
    TIMES,
    canEdit,
    draftEvent,
    eraseHover,
    hoveredUsers,
    setHoveredUsers,
    onDown,
    onEnter,
    startHover,
    stopHover,
    moveHover,
    getUsersAtCell,
    scheduleEventSelection,
    setScheduleEventSelection,
  };
};

export default useGridInteractions;
